import { useEffect, useState } from "react";
import { ArrowClockwise, VideoCamera, PaperPlaneRight, Clock, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function AvailabilityEditor() {
  const [avail, setAvail] = useState(null);
  const [hourInput, setHourInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/settings/availability").then((r) => setAvail(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const toggleDay = (d) =>
    setAvail((a) => ({ ...a, days: a.days.includes(d) ? a.days.filter((x) => x !== d) : [...a.days, d].sort() }));
  const removeHour = (h) => setAvail((a) => ({ ...a, hours_utc: a.hours_utc.filter((x) => x !== h) }));
  const addHour = () => {
    const h = parseInt(hourInput, 10);
    if (Number.isInteger(h) && h >= 0 && h <= 23 && !avail.hours_utc.includes(h)) {
      setAvail((a) => ({ ...a, hours_utc: [...a.hours_utc, h].sort((x, y) => x - y) }));
    }
    setHourInput("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/availability", avail);
      setAvail(data);
      toast.success("Availability updated — the member calendar reflects it now.");
    } catch (e) {
      toast.error("Could not save — pick at least one day and one hour.");
    } finally {
      setSaving(false);
    }
  };

  if (!avail) return null;
  return (
    <div className="mt-6 bg-card border border-border rounded-sm p-7 md:p-8" data-testid="admin-availability-card">
      <div className="flex items-center gap-3">
        <Clock size={20} weight="duotone" className="text-accent" />
        <p className="font-serif text-xl text-primary tracking-tight">Your call hours</p>
      </div>
      <p className="mt-1.5 text-xs text-foreground/50">Members see these slots in their own timezone. Hours are set in UTC (15 = 11am ET / 8am PT).</p>
      <div className="mt-6 flex flex-wrap gap-2.5" data-testid="admin-availability-days">
        {DAY_NAMES.map((name, d) => (
          <button
            key={d}
            data-testid={`admin-day-${d}`}
            onClick={() => toggleDay(d)}
            className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors duration-300 ${
              avail.days.includes(d) ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground/50 hover:border-primary"
            }`}
          >
            {name}
          </button>
        ))}
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-2.5" data-testid="admin-availability-hours">
        {avail.hours_utc.map((h) => (
          <span key={h} className="flex items-center gap-1.5 bg-secondary text-foreground text-xs font-bold rounded-full px-3.5 py-2">
            {String(h).padStart(2, "0")}:00 UTC
            <button data-testid={`admin-hour-remove-${h}`} onClick={() => removeHour(h)} className="text-muted-foreground hover:text-destructive transition-colors">
              <X size={12} weight="bold" />
            </button>
          </span>
        ))}
        <input
          data-testid="admin-hour-input"
          value={hourInput}
          onChange={(e) => setHourInput(e.target.value.replace(/\D/g, "").slice(0, 2))}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addHour())}
          placeholder="Hour 0–23"
          className="w-28 border border-border rounded-full bg-transparent px-3.5 py-2 text-xs font-semibold focus:outline-none focus:border-primary"
        />
        <button data-testid="admin-hour-add" onClick={addHour} className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-primary border border-border rounded-full px-3.5 py-2 hover:border-primary transition-colors">
          <Plus size={12} weight="bold" /> Add
        </button>
      </div>
      <button
        data-testid="admin-availability-save"
        onClick={save}
        disabled={saving}
        className="mt-6 bg-primary text-primary-foreground px-6 py-3 rounded-full font-semibold text-sm hover:bg-accent disabled:opacity-50 transition-colors duration-300"
      >
        {saving ? "Saving…" : "Save availability"}
      </button>
    </div>
  );
}

const STATUS_STYLES = {
  scheduled: "bg-accent/10 text-accent border-accent/30",
  done: "bg-primary/10 text-primary border-primary/30",
  cancelled: "bg-muted text-muted-foreground border-border",
};

const fmt = (iso) =>
  new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function LinkInput({ booking, onSaved }) {
  const [link, setLink] = useState(booking.meeting_link || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (link === (booking.meeting_link || "")) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/bookings/${booking.id}`, { meeting_link: link });
      onSaved(data);
      if (link) toast.success("Link saved — the client has been emailed.");
    } catch {
      toast.error("Could not save the link.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        data-testid={`admin-booking-link-${booking.id}`}
        value={link}
        onChange={(e) => setLink(e.target.value)}
        placeholder="Paste video link…"
        className={`w-56 border border-border rounded-sm bg-transparent px-3 py-1.5 text-xs font-medium text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary transition-colors duration-300 ${saving ? "opacity-50" : ""}`}
      />
      <button
        data-testid={`admin-booking-send-${booking.id}`}
        onClick={save}
        disabled={saving || !link}
        className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary border border-border rounded-full px-3 py-1.5 hover:border-primary disabled:opacity-40 transition-colors duration-300"
      >
        <PaperPlaneRight size={12} weight="bold" /> Send
      </button>
    </div>
  );
}

export default function Bookings() {
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => setError("Could not load bookings."));
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
    } catch {
      setError("Could not update booking.");
    }
  };

  const onLinkSaved = (updated) => {
    setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  };

  return (
    <div data-testid="admin-bookings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-primary tracking-tight">Video call bookings</h1>
          <p className="mt-2 text-sm text-foreground/60" data-testid="admin-bookings-count">
            {bookings ? `${bookings.length} booking${bookings.length === 1 ? "" : "s"}` : "Loading…"}
          </p>
        </div>
        <button
          data-testid="admin-bookings-refresh"
          onClick={load}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
        >
          <ArrowClockwise size={15} weight="bold" /> Refresh
        </button>
      </div>
      {error && <p className="mt-4 text-sm font-semibold text-destructive" data-testid="admin-bookings-error">{error}</p>}

      <AvailabilityEditor />

      <div className="mt-10 bg-card border border-border rounded-sm overflow-x-auto" data-testid="admin-bookings-table-wrap">
        <table className="w-full text-sm min-w-[900px]" data-testid="admin-bookings-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["When", "Client", "Email", "Video Link", "Status"].map((h) => (
                <th key={h} className="px-5 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookings && bookings.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-14 text-center" data-testid="admin-bookings-empty">
                  <VideoCamera size={34} weight="duotone" className="text-muted-foreground mx-auto" />
                  <p className="mt-4 text-sm text-foreground/50 font-medium">No calls booked yet — members book from their portal.</p>
                </td>
              </tr>
            )}
            {bookings &&
              bookings.map((b) => (
                <tr key={b.id} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors duration-200" data-testid={`admin-booking-row-${b.id}`}>
                  <td className="px-5 py-4 font-semibold text-foreground whitespace-nowrap">{fmt(b.slot)}</td>
                  <td className="px-5 py-4 text-foreground/80 whitespace-nowrap">{b.client_name || "—"}</td>
                  <td className="px-5 py-4 text-foreground/70">{b.client_email}</td>
                  <td className="px-5 py-4">
                    <LinkInput booking={b} onSaved={onLinkSaved} />
                  </td>
                  <td className="px-5 py-4">
                    <select
                      data-testid={`admin-booking-status-${b.id}`}
                      value={b.status}
                      onChange={(e) => setStatus(b.id, e.target.value)}
                      className={`text-xs font-bold uppercase tracking-wider border rounded-full px-3 py-1.5 bg-transparent cursor-pointer focus:outline-none ${STATUS_STYLES[b.status] || STATUS_STYLES.scheduled}`}
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="done">Done</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
